import type { PermissionRole } from "@remi/types/permissions";

const InvitableRoles = {
  INVALID: [],
  // REMI_USER carries the cross-hierarchy carve-out (CONTRACTOR/BUYER closure)
  // that the deleted getPermissionRolesEqualAndLower applied dynamically. All
  // other rows are deliberately [self]; do not "fix" the asymmetry.
  REMI_USER: ["REMI_USER", "CONTRACTOR_ADMIN", "CONTRACTOR_PRODUCTION", "BUYER_ADMIN"],
  REMI_OPS: ["REMI_OPS"],
  REMI_SALES: ["REMI_SALES"],
  REMI_SALES_ADMIN: ["REMI_SALES_ADMIN"],
  REMI_ADMIN: ["REMI_ADMIN"],
  REMI_AI_OPERATIONS: ["REMI_AI_OPERATIONS"],
  REMI_AUTOMATIONS: ["REMI_AUTOMATIONS"],
  REMI_ENGINEER: ["REMI_ENGINEER"],
  IMPERSONATOR: ["IMPERSONATOR"],
  // Separation of duties — an auditor must not be able to widen the roster it audits.
  REMI_IMPERSONATION_AUDITOR: [],
  REMI_DESIGNER: ["REMI_DESIGNER"],
  REMI_COMPLIANCE: ["REMI_COMPLIANCE"],
  REMI_COMPLIANCE_ADMIN: ["REMI_COMPLIANCE_ADMIN"],
  REMI_FINANCIAL: ["REMI_FINANCIAL"],
  REMI_FINANCIAL_ADMIN: ["REMI_FINANCIAL_ADMIN"],
  REMI_QUOTE_ADMIN: ["REMI_QUOTE_ADMIN"],
  REMI_REGIONAL_MANAGER: ["REMI_REGIONAL_MANAGER"],
  REMI_DEADLINE_MANAGER: ["REMI_DEADLINE_MANAGER"],
  REMI_PERMISSIONS_MANAGER: [],
  REMI_CHANGE_ORDER_MANAGER: ["REMI_CHANGE_ORDER_MANAGER"],
  REMI_WORK_ORDER_MANAGER: ["REMI_WORK_ORDER_MANAGER"],
  REMI_DISPATCH: ["REMI_DISPATCH"],
  REMI_CHANGE_ORDER_SPECIALIST: ["REMI_CHANGE_ORDER_SPECIALIST"],
  REMI_ACCOUNT_MANAGEMENT_LEAD: ["REMI_ACCOUNT_MANAGEMENT_LEAD"],
  REMI_ACCOUNT_MANAGER: ["REMI_ACCOUNT_MANAGER"],
  REMI_QUOTING: ["REMI_QUOTING"],
  REMI_CLAIM_CAPTURE_TEAM: ["REMI_CLAIM_CAPTURE_TEAM"],
  // Same rationale as HOMEOWNER — machine principal with no admin authority.
  REMI_MCP_USER: [],
  // Machine principals, seeded by script — never invited, and grant no invite authority.
  REMI_MARKETING_WEBSITE: [],
  AUTONOMOUS_AGENT: [],
  // The NOMAD and FIELD_WORKER entries are not invite grants — neither role is in
  // OrganizationTypeToPermissionRoles[CONTRACTOR], so person.create still rejects them.
  // Without them a contractor admin loses the ability to delete their own people once tagged.
  CONTRACTOR_ADMIN: ["CONTRACTOR_ADMIN", "NOMAD_INSPECTION", "NOMAD_QA", "NOMAD_CORRECTION", "FIELD_WORKER"],
  CONTRACTOR_PRODUCTION: ["CONTRACTOR_PRODUCTION"],
  // Qualification markers, not authority.
  NOMAD_INSPECTION: [],
  NOMAD_QA: [],
  NOMAD_CORRECTION: [],
  BUYER_ADMIN: ["BUYER_ADMIN"],
  // End-user role — must never invite or delete-manage any role, even if a
  // future grant of person.invite/person.delete leaks to this row.
  HOMEOWNER: [],
  PARTNER_DEVELOPER: ["PARTNER_DEVELOPER"],
  INSURANCE_ADJUSTER: ["INSURANCE_ADJUSTER"],
  // Same rationale as HOMEOWNER — field-only role with no admin authority.
  FIELD_WORKER: [],
  ESTIMATOR: ["ESTIMATOR"],
  ESTIMATE_REVIEWER: ["ESTIMATE_REVIEWER"],
  ESTIMATE_ADMIN: ["ESTIMATE_ADMIN"],
} as const satisfies Record<PermissionRole, readonly PermissionRole[]>;

// Reused by deletePersonPermissionCheck — invite/delete share the same
// "can manage that role" semantic in this codebase.
export const canRoleManageRole = (granter: PermissionRole, target: PermissionRole): boolean =>
  InvitableRoles[granter].some((role) => role === target);
